/**
 * Refund Policy Page - Chính sách hoàn tiền
 */

import { UnifiedHeader } from '../../components/UnifiedHeader';
import { UnifiedFooter } from '../../components/UnifiedFooter';
import { SEOHead } from '../../components/SEOHead';
import { getCanonicalUrl, getSiteUrl } from '../../utils/siteConfig';
import { StructuredData } from '../../components/StructuredData';
import { RotateCcw, AlertCircle, CheckCircle, XCircle, Mail } from 'lucide-react';
import { motion } from 'framer-motion';

export function RefundPolicyPage() {
  return (
    <div className='bg-background-light dark:bg-background-dark text-text-main-light dark:text-text-main-dark font-body transition-colors duration-200 min-h-screen flex flex-col'>
      <SEOHead
        title='Chính sách hoàn tiền | Hướng nghiệp CAT'
        description='Chính sách đổi trả và hoàn tiền tại Hướng nghiệp CAT'
        canonicalUrl={getCanonicalUrl('/chinh-sach-hoan-tien')}
      />
      <StructuredData
        data={{
          type: 'BreadcrumbList',
          items: [
            { name: 'Trang chủ', url: getSiteUrl() },
            { name: 'Chính sách hoàn tiền', url: getCanonicalUrl('/chinh-sach-hoan-tien') },
          ],
        }}
      />
      <UnifiedHeader />

      <motion.main
        className='flex-1 pt-20 pb-12'
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: 'easeOut' }}
      >
        <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'>
          {/* Header */}
          <div className='text-center mb-12'>
            <h1 className='text-3xl font-bold text-gray-900 dark:text-white mb-4'>
              Chính sách hoàn tiền
            </h1>
            <p className='text-lg text-gray-600 dark:text-gray-300'>
              Cập nhật lần cuối: Tháng 12/2024
            </p>
          </div>

          <div className='space-y-8'>
            {/* Digital Product Notice */}
            <div className='bg-amber-50 dark:bg-amber-900/20 rounded-2xl p-6 border border-amber-200 dark:border-amber-800'>
              <div className='flex items-start gap-4'>
                <AlertCircle className='w-8 h-8 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-1' />
                <div>
                  <h3 className='font-semibold text-amber-800 dark:text-amber-300 mb-2'>
                    Lưu ý về sản phẩm số
                  </h3>
                  <p className='text-amber-700 dark:text-amber-400'>
                    Do đặc thù của sản phẩm số (bài thi trực tuyến, báo cáo điện tử), chính sách
                    hoàn tiền có một số điều kiện cụ thể như sau.
                  </p>
                </div>
              </div>
            </div>

            {/* Refund Policy */}
            <div className='bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-8'>
              <div className='flex items-center gap-3 mb-6'>
                <div className='w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-xl flex items-center justify-center'>
                  <RotateCcw className='w-6 h-6 text-blue-600 dark:text-blue-400' />
                </div>
                <h2 className='text-2xl font-bold text-gray-900 dark:text-white'>
                  Điều kiện hoàn tiền
                </h2>
              </div>

              <div className='space-y-6'>
                {/* Cases with refund */}
                <div>
                  <h3 className='flex items-center gap-2 font-semibold text-green-700 dark:text-green-400 mb-3'>
                    <CheckCircle className='w-5 h-5' />
                    Trường hợp được hoàn tiền 100%
                  </h3>
                  <ul className='space-y-2 ml-7'>
                    {[
                      'Lỗi hệ thống khiến không thể truy cập nội dung đã mua trong thời gian dài',
                      'Thanh toán trùng lặp do lỗi kỹ thuật',
                      'Nội dung không đúng như mô tả trên website',
                    ].map((item, index) => (
                      <li
                        key={index}
                        className='text-gray-600 dark:text-gray-300 flex items-start gap-2'
                      >
                        <span className='text-green-500'>•</span>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Cases without refund */}
                <div>
                  <h3 className='flex items-center gap-2 font-semibold text-red-700 dark:text-red-400 mb-3'>
                    <XCircle className='w-5 h-5' />
                    Trường hợp không được hoàn tiền
                  </h3>
                  <ul className='space-y-2 ml-7'>
                    {[
                      'Đã truy cập và xem nội dung báo cáo đầy đủ',
                      'Đã làm bài thi thử',
                      'Thay đổi ý định sau khi mua',
                      'Không hài lòng với kết quả trắc nghiệm (vì đây là kết quả khách quan dựa trên câu trả lời của bạn)',
                    ].map((item, index) => (
                      <li
                        key={index}
                        className='text-gray-600 dark:text-gray-300 flex items-start gap-2'
                      >
                        <span className='text-red-500'>•</span>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>

            {/* Refund Process */}
            <div className='bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-8'>
              <div className='flex items-center gap-3 mb-6'>
                <div className='w-12 h-12 bg-purple-100 dark:bg-purple-900/30 rounded-xl flex items-center justify-center'>
                  <Mail className='w-6 h-6 text-purple-600 dark:text-purple-400' />
                </div>
                <h2 className='text-2xl font-bold text-gray-900 dark:text-white'>
                  Quy trình yêu cầu hoàn tiền
                </h2>
              </div>

              <ol className='space-y-4'>
                {[
                  {
                    step: 1,
                    title: 'Gửi yêu cầu',
                    desc: 'Email đến support@huongnghiep.cat.edu.vn với tiêu đề "Yêu cầu hoàn tiền"',
                  },
                  {
                    step: 2,
                    title: 'Cung cấp thông tin',
                    desc: 'Mã giao dịch, email đăng ký, lý do hoàn tiền',
                  },
                  {
                    step: 3,
                    title: 'Xác minh',
                    desc: 'Chúng tôi xác minh và phản hồi trong 3-5 ngày làm việc',
                  },
                  {
                    step: 4,
                    title: 'Hoàn tiền',
                    desc: 'Nếu đủ điều kiện, hoàn tiền trong 7-14 ngày làm việc',
                  },
                ].map(({ step, title, desc }) => (
                  <li key={step} className='flex gap-4'>
                    <div className='w-8 h-8 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0'>
                      {step}
                    </div>
                    <div>
                      <h3 className='font-semibold text-gray-900 dark:text-white'>{title}</h3>
                      <p className='text-gray-600 dark:text-gray-300'>{desc}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </div>

            {/* Contact */}
            <div className='bg-gray-50 dark:bg-gray-700/50 rounded-2xl p-6'>
              <h3 className='font-semibold text-gray-900 dark:text-white mb-2'>Liên hệ hỗ trợ</h3>
              <p className='text-gray-600 dark:text-gray-300'>
                Email: <strong>support@huongnghiep.cat.edu.vn</strong>
              </p>
              <p className='text-gray-500 dark:text-gray-400 text-sm mt-2'>
                Thời gian phản hồi: 24-48 giờ làm việc
              </p>
            </div>
          </div>
        </div>
      </motion.main>

      <UnifiedFooter />
    </div>
  );
}

export default RefundPolicyPage;
